import React, { useState, useRef, useEffect, useMemo } from "react";
import "react-datepicker/dist/react-datepicker.css";
import PropTypes from "prop-types";
import { ButtonStyleContainer } from "shared/Modal/SnaptradeModal/SnaptradeModal.styles";
import { SceneriosCard } from "shared/StrategyOverview/Components/SceneriosCard";
import { format, FULL_MONTH_YEAR_FORMAT } from "utils/date";
import { Card } from "components/Card";
import { CalenderIcon } from "assets/icons/index";
import DatePicker from "react-datepicker";
import {
  ActionsContainer,
  ButtonContainer,
  CalendarContainer,
  CancelButton,
  DateInput,
  DateInputsContainer,
  DateInputWrapper,
  DateRangePickerContainer,
  DropdownContainer,
  ErrorMessage,
  ScenariosCardContainer,
  ScenariosPanel,
  ScenariosTitle,
  ShowMoreButton,
  TriggerButton,
  QuickDateContainer,
  QuickDateButtons,
  QuickDateButton,
} from "./Datepicker.style";

const NewCustomDatePicker = ({
  onClick,
  handleMoveCard,
  activeId,
  showSceneriosCount,
  predefinedScenarios = [],
  isLoading,
  handleMoreClick,
  formik,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  const [startInputValue, setStartInputValue] = useState("");
  const [endInputValue, setEndInputValue] = useState("");
  const [isStartInputFocused, setIsStartInputFocused] = useState(false);
  const [isEndInputFocused, setIsEndInputFocused] = useState(false);
  const [activeQuickDate, setActiveQuickDate] = useState(null);
  const containerRef = useRef(null);
  const startDatePickerRef = useRef(null);
  const endDatePickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
      if (
        startDatePickerRef.current &&
        !startDatePickerRef.current.contains(event.target)
      ) {
        setStartDatePickerOpen(false);
      }
      if (
        endDatePickerRef.current &&
        !endDatePickerRef.current.contains(event.target)
      ) {
        setEndDatePickerOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    formik.setFieldValue("start", start);
    formik.setFieldValue("end", end);

    setStartDatePickerOpen(false);
    setEndDatePickerOpen(false);
  };

  const handleStartInputChange = (e) => {
    const value = e.target.value;
    setStartInputValue(value);

    const parsedDate = new Date(value);
    if (!isNaN(parsedDate.getTime()) && value.length >= 8) {
      formik.setFieldValue("start", parsedDate);
    }
  };

  const handleEndInputChange = (e) => {
    const value = e.target.value;
    setEndInputValue(value);

    const parsedDate = new Date(value);
    if (!isNaN(parsedDate.getTime()) && value.length >= 8) {
      formik.setFieldValue("end", parsedDate);
    }
  };

  const handleStartInputKeyDown = (e) => {
    if (e.key === "Enter") {
      const value = e.target.value;
      const parsedDate = new Date(value);
      if (!isNaN(parsedDate.getTime())) {
        formik.setFieldValue("start", parsedDate);
        setStartInputValue(parsedDate.toLocaleDateString());
      }
    }
  };

  const handleEndInputKeyDown = (e) => {
    if (e.key === "Enter") {
      const value = e.target.value;
      const parsedDate = new Date(value);
      if (!isNaN(parsedDate.getTime())) {
        formik.setFieldValue("end", parsedDate);
        setEndInputValue(parsedDate.toLocaleDateString());
      }
    }
  };

  useEffect(() => {
    if (!isStartInputFocused) {
      setStartInputValue(
        formik.values.start ? formik.values.start.toLocaleDateString() : ""
      );
    }
  }, [formik.values.start, isStartInputFocused]);

  useEffect(() => {
    if (!isEndInputFocused) {
      setEndInputValue(
        formik.values.end ? formik.values.end.toLocaleDateString() : ""
      );
    }
  }, [formik.values.end, isEndInputFocused]);
  useEffect(() => {
    const { start, end } = formik.values;

    if (start) {
      formik.setFieldError("start", "");
    }
    if (end) {
      formik.setFieldError("end", "");
    }

    // Validate date range
    if (start && end) {
      const today = new Date();
      today.setHours(23, 59, 59, 999);

      if (start > today) {
        formik.setFieldError("start", "Start date cannot be in the future");
        return;
      }

      if (end > today) {
        formik.setFieldError("end", "End date cannot be in the future");
        return;
      }

      if (start >= end) {
        formik.setFieldError("end", "End date must be after start date");
        return;
      }

      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays < 1) {
        formik.setFieldError("end", "Date range must be at least 1 day");
        return;
      }
    }
  }, [formik.values.start, formik.values.end]);

  const handleApply = () => {
    if (!formik.values.start) {
      formik.setFieldError("start", "Start date is required");
      return;
    }

    if (!formik.values.end) {
      formik.setFieldError("end", "End date is required");
      return;
    }

    const today = new Date();
    today.setHours(23, 59, 59, 999);

    if (formik.values.start > today) {
      formik.setFieldError("start", "Start date cannot be in the future");
      return;
    }

    if (formik.values.end > today) {
      formik.setFieldError("end", "End date cannot be in the future");
      return;
    }

    if (formik.values.start >= formik.values.end) {
      formik.setFieldError("end", "End date must be after start date");
      return;
    }

    // Touch fields to trigger validation display
    formik.setFieldTouched("start", true);
    formik.setFieldTouched("end", true);

    // If all validations pass, close the picker
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  // Disable apply button when a future date is selected
  const isFutureDateSelected = useMemo(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const { start, end } = formik.values;

    return (start && start > today) || (end && end > today);
  }, [formik.values.start, formik.values.end]);

  const disabledTooltipMessage = isFutureDateSelected
    ? "Selected date is in the future. Please choose today or an earlier date."
    : "Apply the selected date range";

  // Quick date selection handlers
  const quickDateOptions = [
    { label: "1Y", years: 1 },
    { label: "3Y", years: 3 },
    { label: "5Y", years: 5 },
    { label: "10Y", years: 10 },
  ];

  const handleQuickDateSelect = (years, label) => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setFullYear(endDate.getFullYear() - years);

    formik.setFieldValue("start", startDate);
    formik.setFieldValue("end", endDate);
    setActiveQuickDate(label);

    // Clear any existing errors
    formik.setFieldError("start", "");
    formik.setFieldError("end", "");
  };

  const handleScenarioClick = (scenario) => {
    setActiveQuickDate(null); // clear quick date selection
    if (onClick) {
      onClick(scenario);
    }
  };

  return (
    <DateRangePickerContainer ref={containerRef}>
      <TriggerButton
        type="button"
        $isOpen={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      >
        <CalenderIcon />
        {formik.values.start && formik.values.end
          ? `${format(FULL_MONTH_YEAR_FORMAT, formik.values.start)} - ${format(
              FULL_MONTH_YEAR_FORMAT,
              formik.values.end
            )}`
          : "Select dates"}
      </TriggerButton>

      <DropdownContainer $isOpen={isOpen}>
        <div className="flex-container">
          {/* left side options */}
          <ScenariosPanel>
            <div className="recommendation-container">
              <ScenariosTitle>Recommended Scenarios</ScenariosTitle>
              <ScenariosCardContainer>
                {predefinedScenarios
                  .slice(0, showSceneriosCount)
                  .map((item, inx) => (
                    <div key={inx}>
                      <SceneriosCard
                        {...item}
                        index={inx}
                        handleMoveCard={handleMoveCard}
                        activeId={activeId}
                        onClick={handleScenarioClick}
                      />
                    </div>
                  ))}
              </ScenariosCardContainer>

              {/* show more button */}
              {!isLoading &&
              predefinedScenarios !== null &&
              predefinedScenarios.length > 0 ? (
                <ShowMoreButton type="button" onClick={handleMoreClick}>
                  {showSceneriosCount === 4 ? "Show more" : "Show less"}
                </ShowMoreButton>
              ) : null}

              {!isLoading && predefinedScenarios.length === 0 && (
                <Card description="No scenarios found" />
              )}
              {isLoading && (
                <Card className="loader-scenario" description="loading..." />
              )}
            </div>

            <QuickDateContainer>
              <QuickDateButtons>
                {quickDateOptions.map((option) => (
                  <QuickDateButton
                    key={option.label}
                    type="button"
                    $isActive={activeQuickDate === option.label}
                    onClick={() =>
                      handleQuickDateSelect(option.years, option.label)
                    }
                  >
                    {option.label}
                  </QuickDateButton>
                ))}
              </QuickDateButtons>
            </QuickDateContainer>
          </ScenariosPanel>

          <CalendarContainer>
            <div className="date-picker-wraper custom-date-picker-wrapper">
              <CustomDatePicker
                selected={formik.values.start}
                onChange={handleDateChange}
                startDate={formik.values.start}
                endDate={formik.values.end}
                selectsRange
                monthsShown={2}
                inline
                showDisabledMonthNavigation={false}
              />
            </div>

            <ActionsContainer>
              <DateInputsContainer>
                <DateInputWrapper ref={startDatePickerRef}>
                  <DateInput
                    value={startInputValue}
                    placeholder="(MM/DD/YYYY)"
                    onChange={handleStartInputChange}
                    onKeyDown={handleStartInputKeyDown}
                    onFocus={() => setIsStartInputFocused(true)}
                    onBlur={() => setIsStartInputFocused(false)}
                    onClick={() => setStartDatePickerOpen(!startDatePickerOpen)}
                    hasError={formik.touched.start && formik.errors.start}
                  />
                  {formik.touched.start && formik.errors.start && (
                    <ErrorMessage>{formik.errors.start}</ErrorMessage>
                  )}
                </DateInputWrapper>
                <span className="dash">—</span>
                <DateInputWrapper ref={endDatePickerRef}>
                  <DateInput
                    value={endInputValue}
                    placeholder="(MM/DD/YYYY)"
                    onChange={handleEndInputChange}
                    onKeyDown={handleEndInputKeyDown}
                    onFocus={() => setIsEndInputFocused(true)}
                    onBlur={() => setIsEndInputFocused(false)}
                    onClick={() => setEndDatePickerOpen(!endDatePickerOpen)}
                    hasError={formik.touched.end && formik.errors.end}
                  />
                  {formik.touched.end && formik.errors.end && (
                    <ErrorMessage>{formik.errors.end}</ErrorMessage>
                  )}
                </DateInputWrapper>
              </DateInputsContainer>

              <ButtonContainer>
                <CancelButton type="button" onClick={handleCancel}>
                  Cancel
                </CancelButton>
                <ButtonStyleContainer>
                  <button
                    type="button"
                    className="btn applyButton"
                    onClick={handleApply}
                    disabled={isFutureDateSelected}
                    title={disabledTooltipMessage}
                  >
                    Apply
                  </button>
                </ButtonStyleContainer>
              </ButtonContainer>
            </ActionsContainer>
          </CalendarContainer>
        </div>
      </DropdownContainer>
    </DateRangePickerContainer>
  );
};

export default NewCustomDatePicker;

NewCustomDatePicker.propTypes = {
  onClick: PropTypes.func,
  handleMoveCard: PropTypes.func,
  activeId: PropTypes.string,
  showSceneriosCount: PropTypes.number,
  isLoading: PropTypes.bool,
  handleMoreClick: PropTypes.func,
  formik: PropTypes.object.isRequired,
  predefinedScenarios: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
      type: PropTypes.string,
      start: PropTypes.number,
      end: PropTypes.number,
      tags: PropTypes.arrayOf(PropTypes.string),
    })
  ),
};

const CustomDatePicker = (props) => {
  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-custom-datepicker", "true");
    style.textContent = `
      .custom-date-picker-wrapper .react-datepicker {
        position: relative;
        border: none !important;
        box-shadow: none !important;
        font-family: inherit !important;
        background: none !important;
        border-radius:none !important;
        font-family: Geist, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        padding:26px 0px 16px;
      }

      .custom-date-picker-wrapper .react-datepicker::after {
        content: '';
        position: absolute;
        top: 0;
        bottom:0px;
        left: 50%;
        transform: translateX(-50%);
        width: 1px;
        height: 100%;
        background-color: #e9eaeb;
        pointer-events: none;
        z-index: 1;
      }

      .custom-date-picker-wrapper .react-datepicker__header {
        background-color: transparent !important;
        border:none !important;
        padding: 0px 0 0px 0 !important;
        border-top-left-radius: 0 !important;
        border-top-right-radius: 0 !important;
      }

      .custom-date-picker-wrapper .react-datepicker__current-month {
        font-size: 14px !important;
        font-weight: 600 !important;
        color: #414651 !important;
        margin-bottom: 14px !important;
      }

      .custom-date-picker-wrapper .react-datepicker__day-names {
        font-family: Geist, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        color: #414651;
        font-size:  14px;
        font-weight: 600;
        margin:0px;
      }

      .custom-date-picker-wrapper .react-datepicker__day-name {
        color: #666 !important;
        font-size: 12px !important;
        font-weight: 500 !important;
        width: 36px !important;
        line-height: 36px !important;
        margin: 2px !important;
      }

      .custom-date-picker-wrapper .react-datepicker__day {
        width: 36px !important;
        line-height: 36px !important;
        font-size: 13px !important;
        color: #333 !important;
        border-radius: 50% !important;
        margin: 2px !important;
        transition: all 0.15s ease !important;
        font-family: Geist, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif !important;
        color: #414651 !important;
        font-size: 14px !important;
        font-weight: 400 !important;
      }

      .custom-date-picker-wrapper .react-datepicker__month{
        height:242px !important;
        background-color: transparent !important;
        border:none !important;
        margin:0px !important;
      }

      .custom-date-picker-wrapper .react-datepicker__day:hover {
        background-color: #e8f0fe !important;
        color: #414651 !important;
      }

      .custom-date-picker-wrapper .react-datepicker__day--selected {
        background: #2258F1 !important;
        color: white !important;
        font-weight: 600 !important;
      }

      .custom-date-picker-wrapper .react-datepicker__day--in-selecting-range,
      .custom-date-picker-wrapper .react-datepicker__day--in-range {
        background-color: #e8f0fe !important;
        color: #2258F1 !important;
        border-radius: 0 !important;
      }

      .custom-date-picker-wrapper .react-datepicker__day--range-start {
        background-color: #2258F1 !important;
        color: white !important;
        border-radius: 50% !important;
        font-weight: 600 !important;
      }

      .custom-date-picker-wrapper .react-datepicker__day--range-end {
        background-color: #2258F1 !important;
        color: white !important;
        border-radius: 50% !important;
        font-weight: 600 !important;
      }

      .custom-date-picker-wrapper .react-datepicker__day--outside-month {
        color: #717680 !important;
        font-family: Geist, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif !important;
        font-size: 14px !important;
        font-weight: 400 !important;
      }

      .custom-date-picker-wrapper .react-datepicker__navigation {
        top: 29px !important;
        width: 28px !important;
        height: 28px !important;
        border-radius: 50% !important;
        background-color: none !important;
        transition: all 0.2s ease !important;
      }

      .custom-date-picker-wrapper .react-datepicker__navigation--previous{
        left:29px !important;
      }
      
      .custom-date-picker-wrapper .react-datepicker__navigation--next{
        right:29px !important;
      }

      .custom-date-picker-wrapper .react-datepicker__navigation:hover {
        background-color: none !important;
        border-color: none !important;
      }

      .custom-date-picker-wrapper .react-datepicker__navigation-icon::before {
        border-color: #A4A7AE !important;
        border-width: 2px 2px 0 0 !important;
        width: 10px !important;
        height: 10px !important;
      }

      .custom-date-picker-wrapper .react-datepicker__month-container {
        margin: 0 20px !important;
        display: inline-block !important;
      }

      .custom-date-picker-wrapper .react-datepicker {
        display: flex !important;
        flex-direction: row !important;
      }

      /* Quick range buttons styling */
      .custom-date-picker-wrapper .quick-range-buttons {
        display: flex;
        gap: 8px;
        margin: 0 20px 8px 20px;
        align-items: center;
        flex-wrap: wrap;
      }
      .custom-date-picker-wrapper .quick-range-button {
        border: 1px solid #e9eaeb;
        background: #fff;
        color: #414651;
        font-size: 12px;
        font-weight: 500;
        padding: 6px 10px;
        border-radius: 20px;
        cursor: pointer;
        transition: all 0.15s ease;
      }
      .custom-date-picker-wrapper .quick-range-button:hover {
        background-color: #f6f8fa;
        border-color: #d7d9de;
      }
      .custom-date-picker-wrapper .quick-range-button:active {
        background-color: #e8f0fe;
        color: #2258F1;
        border-color: #cbd0d6;
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  return <DatePicker {...props} />;
};
